import { Seo } from "@/components/Seo";
import { Reveal, PillButton, Hairline, GlowFrame } from "@/components/primitives";
import { StewardStoneNav } from "@/components/StewardStoneNav";
import { InquiryForm } from "@/components/InquiryForm";

const CAPS = [
  { to: "/steward-and-stone/healthcare-advisory", label: "Healthcare Advisory", testid: "ss-cap-healthcare", d: "Our founding specialization — hospital and healthcare operational consulting: understanding how an organization actually functions and solving complex strategic, organizational, and workflow problems." },
  { to: "/steward-and-stone/operational-strategy", label: "Operational Strategy", testid: "ss-cap-operational", d: "Organizational and operational assessment, workflow and gap analysis, systems redesign, and practical implementation planning across complex environments." },
  { to: "/steward-and-stone/assessment", label: "Organizational Assessment", testid: "ss-cap-assessment", d: "A structured review of how an organization operates — where value is lost, where risk accumulates, and where meaningful transformation is possible." },
];

export default function StewardStone() {
  return (
    <div className="glow-navy min-h-screen">
      <Seo title="Steward & Stone — Strategic & Operational Advisory" description="Steward & Stone — the strategic and operational advisory company of Jennae Michelle Holdings. Healthcare operations advisory, operational strategy, and organizational assessment for complex problems." />
      <StewardStoneNav />

      <section className="pt-10 md:pt-16 pb-8 px-6 text-center">
        <Reveal>
          <h1 className="sr-only">Steward &amp; Stone</h1>
          <img src="/brand/steward-stone-web.png" alt="Steward & Stone — Executive Advisory" className="mx-auto w-full max-w-3xl" />
        </Reveal>
        <Reveal delay={0.12}>
          <div className="mt-8 max-w-2xl mx-auto space-y-5 text-lg md:text-xl text-[var(--bone)]/85 leading-relaxed text-left">
            <p>An organization can appear successful on paper while the people inside it are quietly holding everything together through exhaustion, workarounds, and sheer determination.</p>
            <p>Eventually, the distance between how a system was designed to work and how the work actually gets done becomes too large to ignore. Employees feel it. Customers and patients experience it. Leadership sees the consequences, even when the underlying cause remains difficult to name.</p>
            <p className="text-center font-serif2 italic text-[var(--bone)]">Steward &amp; Stone examines that distance.</p>
            <p>We look at the decisions, workflows, expectations, resources, and relationships shaping the organization's daily reality. The goal is not to hand leadership another polished plan that cannot survive contact with the actual workplace. It is to understand what is happening, why it keeps happening, and what must change for the organization and its people to function well together.</p>
          </div>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-5 max-w-xl mx-auto text-sm md:text-base text-[var(--bone)]/70 leading-relaxed">
            Healthcare and hospital operations are our founding specialization — the capability itself extends to any organization whose systems, workflows, and performance can be assessed and improved.
          </p>
        </Reveal>
      </section>

      <section className="py-14 px-6">
        <div className="max-w-4xl mx-auto">
          <Reveal><Hairline className="opacity-70 mb-12" /></Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
            {CAPS.map((c, i) => (
              <Reveal key={c.to} delay={i * 0.06} className="h-full">
                <GlowFrame to={c.to} testid={c.testid} innerClassName="corner-frame px-7 py-8 h-full flex flex-col transition-transform duration-500 group-hover:-translate-y-[2px]">
                  <h3 className="font-serif2 text-xl md:text-2xl text-[var(--bone)] leading-tight">{c.label}</h3>
                  <p className="mt-4 text-sm md:text-base text-[var(--bone)]/75 leading-relaxed">{c.d}</p>
                  <div className="mt-5 font-display uppercase tracking-wide2 text-[10px] text-[var(--gold-lite)] opacity-0 group-hover:opacity-100 transition-opacity">Enter →</div>
                </GlowFrame>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-6 text-center">
        <Reveal>
          <h2 className="font-display gold-text uppercase tracking-wide2 text-2xl md:text-4xl leading-snug">When Only One Succeeds,<br />None Succeed</h2>
        </Reveal>
        <Reveal delay={0.1} className="mt-8 max-w-3xl mx-auto text-left">
          <p className="font-display uppercase tracking-luxe text-[10px] text-[var(--gold)]/80 text-center mb-6">Success Has More Than One Participant</p>
          <div className="space-y-5 text-lg md:text-xl text-[var(--bone)]/85 leading-relaxed">
            <p>Financial performance matters. So does the team responsible for producing it. So do the people who rely on the organization's work. So does the community living with the consequences of its decisions.</p>
            <p>These responsibilities do not always align automatically. That is precisely why they must be considered together.</p>
            <p>When only one party benefits while everyone else absorbs the cost, the organization may be generating profit—but it has not built something worthy of lasting.</p>
          </div>
        </Reveal>
        <Reveal delay={0.16} className="mt-10"><PillButton to="/steward-and-stone/assessment" testid="ss-request-review">Request a Review</PillButton></Reveal>
      </section>

      <section id="request" className="py-12 px-6">
        <div className="max-w-3xl mx-auto">
          <Reveal className="text-center mb-10"><p className="font-display uppercase tracking-luxe text-xs text-[var(--muted)]">Confidential Inquiry</p></Reveal>
          <Reveal delay={0.05}>
            <InquiryForm type="review" division="Steward & Stone" cta="Request a Review" testid="ss-review-form" />
          </Reveal>
        </div>
      </section>

      <div className="pb-16 text-center px-6">
        <p className="font-display uppercase tracking-wide2 text-[10px] md:text-xs text-[var(--muted)] leading-relaxed">
          Steward &amp; Stone provides strategic and operational advisory only.<br />All inquiries are reviewed confidentially and a response is provided at our discretion.
        </p>
      </div>
    </div>
  );
}
